/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ============================================================================
 * WCAG 2.1 ACCESSIBILITY PATCH — CART LIVE REGION ANNOUNCEMENTS
 * Fixes: WCAG 2.1 SC 4.1.3 (Status Messages) and SC 1.3.1 (Info and Relationships)
 * Ensures JAWS and other screen readers announce cart updates dynamically.
 *
 * Strategy (belt-and-suspenders):
 *   Layer 1 — Focal theme custom events: variant:added, cart:updated
 *   Layer 2 — Fetch API interception: catches /cart/add.js and /cart/change.js
 *             calls made by any third-party cart app (e.g. Globo Cart)
 *   Layer 3 — MutationObserver on cart drawer: announces when drawer opens
 * ============================================================================
 */
(function () {
  'use strict';

  // Deduplicate announcements: track last message and timestamp
  var _lastMessage = '';
  var _lastMessageTime = 0;

  /**
   * Inject the live region into the DOM if it doesn't already exist.
   * The mini-cart.liquid already includes #cart-live-region, but this
   * ensures it is available even before the cart drawer is first opened.
   */
  function ensureLiveRegion() {
    var region = document.getElementById('cart-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'cart-live-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    return region;
  }

  /**
   * Announce a message to screen readers via the ARIA live region.
   * Clears the region first to ensure repeated identical messages are re-announced.
   * Deduplicates identical messages within a 2-second window.
   * @param {string} message - The text to announce.
   */
  function announceToScreenReader(message) {
    var now = Date.now();
    // Deduplicate: skip if same message within 2 seconds
    if (message === _lastMessage && (now - _lastMessageTime) < 2000) {
      return;
    }
    _lastMessage = message;
    _lastMessageTime = now;

    var region = ensureLiveRegion();
    // Clear first so repeated messages are always re-announced by AT
    region.textContent = '';
    // Short timeout allows the DOM mutation to register before new content
    setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  // -------------------------------------------------------------------------
  // LAYER 1: Focal theme custom events
  // -------------------------------------------------------------------------

  /**
   * Listen for variant:added — fires when a product is added to the cart
   * from a product form (product page, quick-buy, etc.) via the Focal theme.
   */
  document.addEventListener('variant:added', function (event) {
    var variant = event.detail && event.detail.variant;
    if (variant) {
      var productTitle = variant.product_title || variant.name || 'Item';
      var variantTitle = (variant.title && variant.title !== 'Default Title') ? ', ' + variant.title : '';
      announceToScreenReader(productTitle + variantTitle + ' has been added to your cart.');
    } else {
      announceToScreenReader('Item has been added to your cart.');
    }
  });

  /**
   * Listen for cart:updated — fires whenever the cart content changes
   * (quantity update, item removal, note change, etc.) via the Focal theme.
   */
  document.addEventListener('cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart) {
      var itemCount = cart.item_count;
      if (itemCount === 0) {
        announceToScreenReader('Your cart is now empty.');
      } else if (itemCount === 1) {
        announceToScreenReader('Cart updated. 1 item in your cart.');
      } else {
        announceToScreenReader('Cart updated. ' + itemCount + ' items in your cart.');
      }
    } else {
      announceToScreenReader('Your cart has been updated.');
    }
  });

  // -------------------------------------------------------------------------
  // LAYER 2: Fetch API interception (catches third-party cart apps)
  // Intercepts /cart/add.js and /cart/change.js AJAX calls made by any
  // cart app (e.g. Globo Cart, Ajax Cart, etc.) and announces the result.
  // -------------------------------------------------------------------------

  var _originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var isCartAdd = url.includes('/cart/add');
    var isCartChange = url.includes('/cart/change') || url.includes('/cart/update');

    var fetchPromise = _originalFetch.apply(this, arguments);

    if (isCartAdd) {
      fetchPromise.then(function (response) {
        // Clone so original response is not consumed
        var cloned = response.clone();
        if (response.ok) {
          cloned.json().then(function (data) {
            // Shopify /cart/add.js returns the added line item
            var title = (data.product_title || data.title || 'Item');
            var variant = (data.variant_title && data.variant_title !== 'Default Title')
              ? ', ' + data.variant_title : '';
            announceToScreenReader(title + variant + ' has been added to your cart.');
          }).catch(function () {
            announceToScreenReader('Item has been added to your cart.');
          });
        }
      }).catch(function () {});
    }

    if (isCartChange) {
      fetchPromise.then(function (response) {
        var cloned = response.clone();
        if (response.ok) {
          cloned.json().then(function (cart) {
            if (cart && typeof cart.item_count !== 'undefined') {
              var count = cart.item_count;
              if (count === 0) {
                announceToScreenReader('Your cart is now empty.');
              } else if (count === 1) {
                announceToScreenReader('Cart updated. 1 item in your cart.');
              } else {
                announceToScreenReader('Cart updated. ' + count + ' items in your cart.');
              }
            }
          }).catch(function () {});
        }
      }).catch(function () {});
    }

    return fetchPromise;
  };

  // -------------------------------------------------------------------------
  // LAYER 3: MutationObserver — focus management when cart drawer opens
  // Moves keyboard focus to the close button when the drawer opens,
  // so JAWS reads the dialog label and orients the user immediately.
  // -------------------------------------------------------------------------

  function initCartDrawerFocusManagement() {
    var miniCart = document.getElementById('mini-cart');
    if (!miniCart) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          var isOpen = miniCart.hasAttribute('open');
          if (isOpen) {
            // Focus the close button so JAWS reads the dialog label
            var closeButton = miniCart.querySelector('[data-action="close"]');
            if (closeButton) {
              setTimeout(function () {
                closeButton.focus();
              }, 150);
            }
          }
        }
      });
    });

    observer.observe(miniCart, { attributes: true });
  }

  // Run focus management setup immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawerFocusManagement);
  } else {
    initCartDrawerFocusManagement();
  }

}());
